import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from '@blocs.i18n';
import { BasicContainer, CircleButton, FlexCol, FlexRow, TextDynamic, TextDynamicProps } from '@minecraft.atoms';
import { CircleDropdownButton } from './DropdownButton';

const BUTTON_TYPES = {
  STANDARD: 'standard',
  DROPDOWN: 'dropdown',
} as const;

type BaseButtonConfig = {
  key: string;
  qaId?: string;
  iconName: string;
  label: string;
};

// would render a button that just has a click handler
type StandardButtonConfig = {
  type: typeof BUTTON_TYPES.STANDARD;
  handleClick: () => void;
} & BaseButtonConfig;

// props for a dropdown button that opens a menu with content
type DropdownButtonConfig = {
  type: typeof BUTTON_TYPES.DROPDOWN;
  menuChildren: React.ReactNode;
  menuAlign?: 'right' | 'left';
} & BaseButtonConfig;

type HeaderButtonProps = StandardButtonConfig | DropdownButtonConfig;

const PartialCollapseHeaderButton = (button: HeaderButtonProps) => {
  if (button.type === BUTTON_TYPES.DROPDOWN) {
    return (
      <CircleDropdownButton
        qaId={button.qaId}
        toggleProps={{ size: 'xs', label: button?.label, icon: button?.iconName }}
        menuItemsProps={[]}
        menuAlign={button?.menuAlign || 'right'}
        menuChildren={<BasicContainer w="max">{button.menuChildren}</BasicContainer>}
      />
    );
  }

  return (
    <CircleButton
      size="xs"
      label={button.label}
      icon={button.iconName}
      onClick={button.handleClick}
      data-qa-id={button.qaId}
    />
  );
};

type ControlledProps = {
  /** whether the panel is toggled open (controlled component usage) */
  isOpen: boolean;
  /** callback handler for the toggle event when isOpen is set */
  onToggle: () => void;
  /** initialOpen cannot be used when isOpen is set */
  initialOpen?: never;
};

type UncontrolledProps = {
  /** isOpen cannot be used when initialOpen is set */
  isOpen?: never;
  /** onToggle cannot be used when initialOpen is set */
  onToggle?: never;
  /** set the initial value of the panel's state in uncontrolled mode
   * (this prop is ignored if isOpen is set)
   * defaults to true
   */
  initialOpen?: boolean;
};

type PartialCollapseStateProps = ControlledProps | UncontrolledProps;

export type PartialCollapseProps = PartialCollapseStateProps & {
  /** content to render even when the component is toggled "closed"
   * if the content is taller than the height specified by `partialHeight` (~200px by default)
   * it will have a gradient overlaid to indicate there is more content
   * to see when toggled open
   */
  partialContent?: React.ReactNode;
  /** height value for the partial content's height when in "closed" state, defaults to '50' (= 12.5rem / 200px), must have a valid value for the string in `cn_h-*` in global_overrides.css */
  partialHeight?: string;
  /** Component to render if the partial content overflows the max height when the component is "closed", is able to invoke a callback to toggle the content into the "open" state
   * this component is not rendered if the partial content does not overflow or if the panel is "open"
   */
  OverflowContent?: React.FC<{ onToggle?: () => void }>;
  /** content to render only when the component is "open"  */
  children?: React.ReactNode;
  /** label value for the panel header */
  label?: string;
  /** props to set the size, color, weight, etc of the label if the default does not work */
  labelProps?: TextDynamicProps;
  /** header buttons */
  headerButtons?: HeaderButtonProps[];
  /** qa id value for the wrapper around the entire collapse toggle & panel */
  'data-qa-id'?: string;
  /** testid value for the wrapper around the entire collapse toggle & panel */
  'data-testid'?: string;
  /** Content to render below the partial content
   * This content will be displayed regardless of whether the component is expanded or collapsed,
   * positioned below the partial content section
   */
  footerContent?: React.ReactNode;
};

/**
 * PartialCollapse
 * a collapsible panel component that even when "closed" will render a portion of the content (the first ~200px of vertically)
 * and when opened it will render the entire content
 */
export const PartialCollapse = ({
  'data-qa-id': qaId,
  'data-testid': testId,
  label,
  partialContent,
  partialHeight = '50',
  OverflowContent,
  children,
  onToggle,
  initialOpen = false,
  isOpen: isOpenProp,
  headerButtons,
  footerContent,
}: PartialCollapseProps) => {
  const [isOpenStateVal, setOpen] = useState(initialOpen);
  const isOpen = typeof isOpenProp === 'boolean' ? isOpenProp : isOpenStateVal;
  const partialContainerRef = useRef<HTMLDivElement>();
  const [isPartialOverflowed, setPartialOverflowed] = useState(false);
  const [hasPartialContent, setHasPartialContent] = useState(false);
  const { t } = useTranslation();
  const hasFooterContent = !!footerContent;

  useLayoutEffect(() => {
    const partialContainer = partialContainerRef?.current;

    if (partialContainer?.clientHeight > 0 && !hasPartialContent) {
      setHasPartialContent(true);
    }

    if (partialContainer?.scrollHeight > partialContainer?.clientHeight && !isPartialOverflowed && !isOpen) {
      setPartialOverflowed(true);
    }

    if (partialContainer?.scrollHeight <= partialContainer?.clientHeight && isPartialOverflowed && !isOpen) {
      setPartialOverflowed(false);
    }
  }, [hasPartialContent, partialContent, children, isOpen, isPartialOverflowed]);

  const toggle = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();

      if (typeof isOpenProp === 'boolean') {
        onToggle?.();

        return;
      }

      setOpen(!isOpen);
    },
    [isOpen, isOpenProp, onToggle]
  );

  const hasHiddenContent = !!children;
  const showToggle = hasHiddenContent || (hasPartialContent && isPartialOverflowed);
  const showHeaderButtons = showToggle || headerButtons?.length > 0;
  const testIdBase = testId || 'partial-collapse';

  return (
    <FlexCol gap="4" w="full" wrap="nowrap" data-testid={`${testIdBase}-wrapper`} data-qa-id={qaId || testIdBase}>
      <BasicContainer alignItems="center" w="full" data-testid={`${testIdBase}-header`}>
        {showHeaderButtons && (
          <FlexRow
            justifyContent="end"
            gap="2"
            ml="4"
            data-testid={`${testIdBase}-action-buttons`}
            className="cn_float-end" // this float is important so that the header text wraps around the buttons on mobile if the text is too long
          >
            {showToggle && (
              <CircleButton
                size="xs"
                label={isOpen ? t('common:button.showLess') : t('common:button.showMore')}
                icon={isOpen ? 'chevron-open-thick' : 'chevron-closed-thick'}
                onClick={toggle}
              />
            )}
            {Array.isArray(headerButtons) &&
              headerButtons.map((button) => <PartialCollapseHeaderButton key={button.key} {...button} />)}
          </FlexRow>
        )}
        <TextDynamic fontSize="h2" fontWeight="medium" as="p">
          {label}
        </TextDynamic>
      </BasicContainer>
      <FlexCol gap="4" w="full" data-testid={`${testIdBase}-content`}>
        <BasicContainer
          ref={partialContainerRef}
          w="full"
          maxH={isOpen ? undefined : partialHeight}
          className="cn_overflow-hidden cn_relative"
          data-testid={`${testIdBase}-partial-content`}
        >
          {partialContent}
          {!isOpen && isPartialOverflowed && (
            <BasicContainer
              w="full"
              className="cn_absolute cn_gradient-bg-partial-collapse-overflow"
              bottom={0}
              left={0}
              right={0}
              h="14"
              data-testid={`${testIdBase}-overflow-overlay`}
            >
              {OverflowContent ? <OverflowContent onToggle={() => toggle()} /> : null}
            </BasicContainer>
          )}
        </BasicContainer>
        {isOpen && (
          <BasicContainer w="full" data-testid={`${testIdBase}-collapse-content`}>
            {children}
          </BasicContainer>
        )}
      </FlexCol>
      {hasFooterContent && (
        <FlexCol gap="4" w="full" data-testid={`${testIdBase}-footer-content`}>
          {footerContent}
        </FlexCol>
      )}
    </FlexCol>
  );
};
